import React, { useState, useEffect } from "react";
import modeloImage from "../modelo.webp";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from '@mui/icons-material/Download';
import "./DataPage.css"; // Ensure styles are included
import pt from "../i18n/pt.json";
import en from "../i18n/en.json";
import de from "../i18n/de.json";
import { useLanguage } from "../context/LanguageContext";
import Swal from "sweetalert2";

const API_BASE_URL = "https://scheduleapp-backend.whitemoss-45cf5382.westeurope.azurecontainerapps.io";

function ModelPage() {
  const { t } = useLanguage();
  
  const [csvName, setCsvName] = useState("exemplo");
  const [isRunning, setIsRunning] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState(""); // 'success' or 'error'
  const [fileList, setFileList] = useState([]);

  const [uploadedCsv, setUploadedCsv] = useState(null); // 🚀 New state for uploaded file
  const [EditCsvName, setEditCsvName] = useState("");
  const [cutoffDate, setCutoffDate] = useState("");
  const [rerunFilename, setRerunFilename] = useState("");

  // Fetch available files when the component mounts
  useEffect(() => {
    fetchFiles();
  }, []);

  async function fetchFiles() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/files`);
      const data = await res.json();
      if (res.ok) {
        setFileList(data.files);
      } else {
        console.error("Failed to fetch files");
      }
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  }

  async function handleRunModel() {
    setIsRunning(true);
    setStatusMessage("");
    setStatusType("");
  
    try {
      const startResp = await fetch(`${API_BASE_URL}/api/model`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ result_filename: csvName }),
      });
  
      if (!startResp.ok) throw new Error(t("model.fail_initiliaze"));
  
      const { job_id } = await startResp.json();
  
      const checkStatus = async () => {
        const statusResp = await fetch(`${API_BASE_URL}/api/job/${job_id}`, {
          method: "GET"
        });
        if (!statusResp.ok) throw new Error(t("model.fail_check_status"));
  
        const data = await statusResp.json();
  
        if (data.status === "running") {
          setTimeout(checkStatus, 5000); // Retry after 5 seconds
        } else if (data.status === "completed") {
          setStatusMessage(`${t("model.success")}: ${data.file}`);
          setStatusType("success");
          fetchFiles(); // Refresh file list
          setIsRunning(false); // Stop loading overlay after completion
        } else {
          setStatusMessage(t("model.fail_execute"));
          setStatusType("error");
          setIsRunning(false); // Stop loading overlay on failure
        }
      };
  
      checkStatus();
    } catch (err) {
      console.error("Erro na execução:", err);
      setStatusMessage(t("model.fail_server"));
      setStatusType("error");
      setIsRunning(false); // Stop loading overlay on error
    }
  }
  
  async function handleRerunModel() {
    if ((!EditCsvName && !uploadedCsv) || !cutoffDate || !rerunFilename) {
      setStatusMessage(t("model.fill_fields_error"));
      setStatusType("error");
      return;
    }
  
    setIsRunning(true);
    setStatusMessage("");
    setStatusType("");
  
    try {
      let input_filename = "";
  
      if (uploadedCsv) {
        // If user uploaded a file, upload it first
        const formData = new FormData();
        formData.append("file", uploadedCsv);
  
        const uploadResp = await fetch(`${API_BASE_URL}/api/upload`, {
          method: "POST",
          body: formData,
        });
  
        if (!uploadResp.ok) throw new Error(t("model.upload_fail"));
  
        const { filename: serverUploadedFilename } = await uploadResp.json();
        input_filename = serverUploadedFilename;
      } else {
        // Otherwise use selected file
        input_filename = EditCsvName;
      }
  
      // Now start rerun model with the correct file
      const resp = await fetch(`${API_BASE_URL}/api/model/from_file`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          input_filename: input_filename,
          cutoff_date: cutoffDate,
          result_filename: rerunFilename,
        }),
      });
  
      if (!resp.ok) throw new Error(t("model.fail_initialize_rerun"));
  
      const { job_id } = await resp.json();
  
      const checkStatus = async () => {
        const statusResp = await fetch(`${API_BASE_URL}/api/job/${job_id}`);
        if (!statusResp.ok) throw new Error(t("model.fail_check_status"));
  
        const data = await statusResp.json();
  
        if (data.status === "running") {
          setTimeout(checkStatus, 5000);
        } else if (data.status === "completed") {
          setStatusMessage(`${t("model.reexecute_success")}: ${data.file}`);
          setStatusType("success");
          fetchFiles(); // refresh list
          setIsRunning(false);
        } else {
          setStatusMessage(t("model.reexecute_fail"));
          setStatusType("error");
          setIsRunning(false);
        }
      };
  
      checkStatus();
    } catch (err) {
      console.error("Erro ao reexecutar:", err);
      setStatusMessage(t("model.reexecute_fail_server"));
      setStatusType("error");
      setIsRunning(false);
    }
  }  

  async function handleDeleteFile(filename) {
    const result = await Swal.fire({
        title: `${t("model.delete_check")}: ${filename}?`,
        text: t("data.undo_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("common.delete"),
        cancelButtonText: t("common.cancel"),
        reverseButtons: true,
      });
    
      if (!result.isConfirmed) return; // Stop if user cancels

    try {
      const res = await fetch(`${API_BASE_URL}/api/files/${filename}`, {
        method: "DELETE",
      });

      if (res.ok) {
        setFileList((prevFiles) => prevFiles.filter((file) => file !== filename));
        setStatusMessage(`${t("model.delete_sucess")}: ${filename}`);
        setStatusType("success");
      } else {
        setStatusMessage(t("model.delete_fail"));
        setStatusType("error");
      }
    } catch (err) {
      console.error("Erro ao excluir ficheiro:", err);
      setStatusMessage(t("model.network_fail"));
      setStatusType("error");
    }
  }

  async function handleDownloadFile(filename) {
    try {
      const downloadUrl = `${API_BASE_URL}/api/download/${encodeURIComponent(filename)}`;
  
      // Create a temporary <a> tag to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading file:", err);
      setStatusMessage(t("model.download_fail"));
      setStatusType("error");
    }
  }  

  return (
    <div className="model-container">
      {/* Header image */}
      <img
        src={modeloImage}
        alt="AI Scheduling Illustration"
        className="model-header-image"
      />

      <h2 className="model-title">{t("model.execute_model_title")}</h2>

      <div className="input-container">
        <label className="model-label">{t("model.file_name")}:</label>
        <input
          type="text"
          value={csvName}
          onChange={(e) => setCsvName(e.target.value)}
          className="model-input"
        />
      </div>

      <button onClick={handleRunModel} className="model-execute-button">
      {t("model.execute_model_button")}
      </button>

      {/* Loading Overlay */}
      {isRunning && (
        <div className="overlay">
          <div className="overlay-content">
            <div className="spinner"></div>
            <p>{t("model.executing")}</p>
          </div>
        </div>
      )}

      {/* Status Message with Icons */}
      {statusMessage && (
        <p className={`status-message ${statusType}`}>
          {statusType === "success" ? "✅" : "❌"} {statusMessage}
        </p>
      )}

      {/* Output Files Table */}
      <h3 className="files-title">{t("model.schedules_list")}</h3>
      <table className="file-table">
        <tbody>
          {fileList.map((file, index) => (
            <tr key={index}>
              <td className="file-item">
                {file}
                <div>
                  <button
                    className="download-button"
                    onClick={() => handleDownloadFile(file)}
                    style={{ marginRight: "8px" }}
                    disabled={isRunning}
                  >
                    <DownloadIcon />
                  </button>
                  <button className="delete-button" onClick={() => handleDeleteFile(file)}>
                    <CloseIcon />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="files-title" style={{ marginTop: "2rem", paddingTop: "1rem", borderTop: "1px solid #ccc"}}>{t("model.edit_schedule_section")}</h3>
      <div className="rerun-form">
        <div style={{ display: "flex" }}>
          <div className="input-container">
            <label className="model-label">{t("model.edit_schedule_file")}:</label>
            <select
              className="model-input"
              value={EditCsvName}
              onChange={(e) => setEditCsvName(e.target.value)}
            >
              <option value="">{t("common.none")}</option> {/* ✅ Added None */}
              {fileList.map((file) => (
                <option key={file} value={file}>
                  {file}
                </option>
              ))}
            </select>
          </div>

          <div className="input-container">
            <label className="model-label">{t("model.upload_csv_label")}:</label>
            <input
              type="file"
              accept=".csv"
              className="model-input"
              style={{ padding: "0" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setUploadedCsv(file);
                } else {
                  setUploadedCsv(null);
                }
              }}
            />
          </div>
        </div>

        <div className="input-container">
          <label className="model-label">{t("model.edit_schedule_date")}:</label>
          <input
            type="date"
            className="model-input"
            value={cutoffDate}
            onChange={(e) => setCutoffDate(e.target.value)}
          />
        </div>

        <div className="input-container">
          <label className="model-label">{t("model.edit_schedule_new_file")}:</label>
          <input
            type="text"
            className="model-input"
            value={rerunFilename}
            onChange={(e) => setRerunFilename(e.target.value)}
            placeholder="ex: test"
          />
        </div>

        <button onClick={handleRerunModel} className="model-execute-button" style={{ width: "100%" }}>
        {t("model.edit_schedule_rerun_button")}
        </button>
      </div>
    </div>
  );
}

export default ModelPage;
